(function () {
  'use strict';

  if (window.LuxsterCatalogMenuController) {
    window.LuxsterCatalogMenuController.init();
    return;
  }

  var desktop = window.matchMedia('(min-width: 1200px)');
  var hover = window.matchMedia('(hover: hover) and (pointer: fine)');
  var entries = [];
  var active = null;
  var closeTimer = null;
  var nextId = 0;
  var drawer = null;
  var scrollLock = null;
  var boundHeaders = new WeakSet();
  var boundOtherItems = new WeakSet();
  var boundDrawers = new WeakSet();

  function clearCloseTimer() {
    window.clearTimeout(closeTimer);
    closeTimer = null;
  }

  function setOpen(entry, open) {
    clearCloseTimer();
    if (open && desktop.matches) {
      entries.forEach(function (other) {
        if (other !== entry) {
          other.details.open = false;
          other.summary.setAttribute('aria-expanded', 'false');
        }
      });
    }
    entry.details.open = open;
    entry.summary.setAttribute('aria-expanded', String(open));
    if (open && desktop.matches) active = entry;
    else if (active === entry) active = null;
  }

  function closeAll() {
    clearCloseTimer();
    entries.forEach(function (entry) {
      entry.details.open = false;
      entry.summary.setAttribute('aria-expanded', 'false');
    });
    active = null;
  }

  // Lock the root scroller without changing the page or sticky header's positioning.
  // In particular, never fix body: doing so resets scrollY and restacks the navigation.
  function lockScroll() {
    if (scrollLock) return;
    scrollLock = { x: window.scrollX, y: window.scrollY, observer: null };
    document.documentElement.classList.add('luxster-mobile-menu-open');
    if (window.ResizeObserver) {
      scrollLock.observer = new ResizeObserver(positionDrawer);
      scrollLock.observer.observe(drawer.opener.closest('header'));
    }
  }

  function unlockScroll() {
    if (!scrollLock) return;
    var saved = scrollLock;
    scrollLock = null;
    if (saved.observer) saved.observer.disconnect();
    document.documentElement.classList.remove('luxster-mobile-menu-open');
    if (window.scrollX !== saved.x || window.scrollY !== saved.y) {
      window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
    }
  }

  function stopDrawerAnimation(state) {
    if (!drawer) return;
    if (state && drawer.motion) drawer.panel.style.opacity = state.opacity;
    if (state && drawer.contentMotion) drawer.content.style.transform = state.transform;
    if (drawer.motion) { drawer.motion.onfinish = null; drawer.motion.cancel(); drawer.motion = null; }
    if (drawer.contentMotion) { drawer.contentMotion.cancel(); drawer.contentMotion = null; }
  }

  function drawerFrame() {
    if (!drawer.panel.classList.contains('show')) return { opacity: '0', transform: 'translateY(10px)' };
    return { opacity: window.getComputedStyle(drawer.panel).opacity, transform: window.getComputedStyle(drawer.content).transform };
  }

  function animateDrawer(from, opening, finish) {
    drawer.panel.style.opacity = from.opacity;
    drawer.content.style.transform = from.transform;
    if (!drawer.panel.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      drawer.panel.style.removeProperty('opacity');
      drawer.content.style.removeProperty('transform');
      if (finish) finish();
      return;
    }
    var timing = { duration: 200, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'both' };
    var animation = drawer.panel.animate([{ opacity: from.opacity }, { opacity: opening ? 1 : 0 }], timing);
    drawer.contentMotion = drawer.content.animate([{ transform: from.transform }, { transform: opening ? 'translateY(0)' : 'translateY(10px)' }], timing);
    drawer.motion = animation;
    animation.onfinish = function () {
      if (drawer.motion !== animation) return;
      drawer.motion = null;
      if (drawer.contentMotion) { drawer.contentMotion.cancel(); drawer.contentMotion = null; }
      drawer.panel.style.removeProperty('opacity');
      drawer.content.style.removeProperty('transform');
      if (finish) finish();
      animation.cancel();
    };
  }

  function finishClosingDrawer(restoreFocus, wasOpen) {
    drawer.closing = false;
    drawer.panel.classList.remove('show', 'collapsing');
    drawer.panel.classList.add('collapse');
    drawer.panel.style.removeProperty('opacity');
    drawer.content.style.removeProperty('transform');
    drawer.panel.style.removeProperty('height');
    drawer.panel.removeAttribute('aria-modal');
    drawer.panel.removeAttribute('role');
    if (desktop.matches) drawer.panel.removeAttribute('inert');
    else drawer.panel.setAttribute('inert', '');
    // Restore the underlying page before the next interaction can scroll it.
    unlockScroll();
    if (restoreFocus && wasOpen && drawer.opener.isConnected) drawer.opener.focus({ preventScroll: true });
  }

  function closeDrawer(restoreFocus, immediate) {
    if (!drawer) return;
    var wasOpen = drawer.open;
    var visible = drawer.panel.classList.contains('show');
    var from = drawerFrame();
    stopDrawerAnimation(from);
    drawer.open = false;
    drawer.opener.setAttribute('aria-expanded', 'false');
    drawer.opener.setAttribute('aria-label', drawer.opener.getAttribute('data-menu-open-label'));
    drawer.panel.setAttribute('inert', '');
    if (!immediate && wasOpen && visible) {
      drawer.closing = true;
      animateDrawer(from, false, function () { finishClosingDrawer(restoreFocus, wasOpen); });
    } else {
      finishClosingDrawer(restoreFocus, wasOpen);
    }
  }

  function positionDrawer() {
    if (!drawer || (!drawer.open && !drawer.closing)) return;
    var header = drawer.opener.closest('header');
    var viewport = window.visualViewport;
    var viewportTop = viewport ? viewport.offsetTop : 0;
    var viewportBottom = viewportTop + (viewport ? viewport.height : window.innerHeight);
    var navigation = header.querySelector('.middle-header') || header;
    var top = Math.max(viewportTop, Math.min(viewportBottom, navigation.getBoundingClientRect().bottom));
    drawer.panel.style.setProperty('--luxster-mobile-menu-top', top + 'px');
    drawer.panel.style.setProperty('--luxster-mobile-menu-height', Math.max(0, viewportBottom - top) + 'px');
    if (window.LuxsterHeaderHeightSync) window.LuxsterHeaderHeightSync.sync();
  }

  function openDrawer() {
    if (!drawer || desktop.matches) return;
    var from = drawerFrame();
    stopDrawerAnimation(from);
    drawer.closing = false;
    drawer.open = true;
    drawer.panel.classList.remove('collapsing');
    drawer.panel.classList.add('collapse');
    drawer.panel.style.removeProperty('height');
    drawer.panel.setAttribute('role', 'region');
    // Complete the scroll/header layout while the drawer is still hidden.
    // Its first visible layout and the icon animation must use the final geometry.
    lockScroll();
    positionDrawer();
    drawer.panel.removeAttribute('inert');
    drawer.panel.classList.add('show');
    drawer.opener.setAttribute('aria-expanded', 'true');
    drawer.opener.setAttribute('aria-label', drawer.opener.getAttribute('data-menu-close-label'));
    animateDrawer(from, true);
    drawer.close.focus({ preventScroll: true });
  }

  function initDrawer(header) {
    var panel = header.querySelector('#navbarSidetoggle');
    var opener = header.querySelector('.side_navbar_toggler');
    var close = opener;
    if (!panel || !opener || !close || boundDrawers.has(panel)) return;
    if (drawer && drawer.panel !== panel) closeDrawer(false, true);
    boundDrawers.add(panel);
    drawer = { panel: panel, content: panel.querySelector(':scope > .navbar-nav') || panel, opener: opener, close: close, open: false, closing: false, motion: null, contentMotion: null };
    opener.removeAttribute('data-toggle');
    opener.removeAttribute('data-target');
    opener.setAttribute('aria-controls', panel.id);
    opener.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      if (drawer.open) closeDrawer(true);
      else openDrawer();
    });
    panel.addEventListener('click', function (event) {
      event.stopPropagation();
      var anchor = event.target.closest('a[href]');
      if (anchor && anchor.getAttribute('href') && anchor.getAttribute('href') !== '#') closeDrawer(false);
    });
    closeDrawer(false, true);
  }

  function scheduleClose(entry) {
    clearCloseTimer();
    closeTimer = window.setTimeout(function () {
      closeTimer = null;
      if (active === entry) setOpen(entry, false);
    }, 180);
  }

  function canHover(event) {
    return desktop.matches && hover.matches && event.pointerType === 'mouse';
  }

  function init() {
    entries = entries.filter(function (entry) { return entry.item.isConnected; });
    if (active && !active.item.isConnected) active = null;
    if (drawer && !drawer.panel.isConnected) {
      closeDrawer(false, true);
      drawer = null;
    }
    document.querySelectorAll('header.header_custom').forEach(initDrawer);
    document.querySelectorAll('header.header_custom [data-catalog-menu]').forEach(function (item) {
      if (item.dataset.catalogReady === 'true') return;
      var details = item.querySelector('details');
      var summary = details && details.querySelector('summary');
      var panel = details && details.querySelector('.luxster-catalog-menu__panel');
      var header = item.closest('header.header_custom');
      if (!details || !summary || !panel || !header) return;
      item.dataset.catalogReady = 'true';
      if (!panel.id) panel.id = 'LuxsterCatalogMenu-' + (++nextId);
      summary.setAttribute('aria-controls', panel.id);
      summary.setAttribute('aria-expanded', String(details.open));
      var entry = { item: item, details: details, summary: summary, panel: panel, header: header };
      entries.push(entry);
      summary.addEventListener('click', function (event) {
        event.preventDefault();
        setOpen(entry, !details.open);
      });
      summary.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        setOpen(entry, true);
        var links = panel.querySelectorAll('a[href]');
        var target = event.key === 'ArrowUp' ? links[links.length - 1] : links[0];
        if (target) target.focus({ preventScroll: true });
      });
      details.addEventListener('toggle', function () {
        summary.setAttribute('aria-expanded', String(details.open));
        if (desktop.matches && details.open && active !== entry) setOpen(entry, true);
        else if (!details.open && active === entry) active = null;
      });
      item.addEventListener('pointerenter', function (event) {
        if (canHover(event)) setOpen(entry, true);
      });
      item.addEventListener('pointerleave', function (event) {
        if (!canHover(event) || !details.open) return;
        if (event.relatedTarget instanceof Node && header.contains(event.relatedTarget)) {
          clearCloseTimer();
          return;
        }
        scheduleClose(entry);
      });
      panel.addEventListener('pointerenter', clearCloseTimer);
      if (!boundHeaders.has(header)) {
        boundHeaders.add(header);
        header.addEventListener('pointerenter', function (event) {
          if (canHover(event)) clearCloseTimer();
        });
        header.addEventListener('pointerleave', function (event) {
          if (canHover(event) && active && active.header === header) scheduleClose(active);
        });
      }
      header.querySelectorAll('#navbarSidetoggle > .navbar-nav > li:not([data-catalog-menu])').forEach(function (otherItem) {
        if (boundOtherItems.has(otherItem)) return;
        boundOtherItems.add(otherItem);
        otherItem.addEventListener('pointerenter', function (event) {
          if (canHover(event)) closeAll();
        });
      });
    });
  }

  document.addEventListener('pointerdown', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (drawer && drawer.open && !drawer.panel.contains(target) && !drawer.opener.contains(target)) closeDrawer(false);
    // Keep in-flow mobile grids stable until the pressed control activates.
    if (!desktop.matches) return;
    if (!active) return;
    if (target.closest('[data-header-search-toggle], [data-vehicle-toggle], .side_navbar_toggler, .cart_trigger')) {
      closeAll();
      return;
    }
    if (!active.item.contains(target) && !target.closest('[data-catalog-menu]')) closeAll();
  }, true);

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (target && drawer && drawer.open && !drawer.panel.contains(target) && !drawer.opener.contains(target)) closeDrawer(false);
  }, true);

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!desktop.matches || !target || !active) return;
    if (target.closest('[data-header-search-toggle], [data-vehicle-toggle], .side_navbar_toggler, .cart_trigger')) {
      closeAll();
      return;
    }
    if (!active.item.contains(target) && !target.closest('[data-catalog-menu]')) closeAll();
  });

  document.addEventListener('keydown', function (event) {
    if (event.defaultPrevented) return;
    if (drawer && drawer.open && event.key === 'Tab') {
      var focusable = Array.from(drawer.opener.closest('header').querySelectorAll('a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(function (element) {
        return element.getClientRects().length && !element.closest('[hidden], [inert]');
      });
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    if (event.key !== 'Escape') return;
    if (desktop.matches && active) {
      var entry = active;
      event.preventDefault();
      closeAll();
      entry.summary.focus({ preventScroll: true });
    } else if (drawer && drawer.open) {
      event.preventDefault();
      closeDrawer(true);
    }
  });

  document.addEventListener('focusin', function (event) {
    if (drawer && (drawer.open || drawer.closing) && !drawer.opener.closest('header').contains(event.target)) {
      drawer.close.focus({ preventScroll: true });
      return;
    }
    // Focusing another drawer control must not move it before pointerup.
    if (drawer && (drawer.open || drawer.closing) && !desktop.matches && (drawer.panel.contains(event.target) || drawer.opener.contains(event.target))) return;
    if (active && !active.item.contains(event.target) && !event.target.closest('[data-catalog-menu]')) closeAll();
  });

  desktop.addEventListener('change', function () { closeDrawer(false, true); closeAll(); });
  hover.addEventListener('change', function () { if (desktop.matches) closeAll(); });
  var lastTouchY = 0;
  document.addEventListener('touchstart', function (event) {
    if (event.touches.length === 1) lastTouchY = event.touches[0].clientY;
  }, { passive: true });
  document.addEventListener('touchmove', function (event) {
    if (!scrollLock || !drawer || event.touches.length !== 1) return;
    var y = event.touches[0].clientY;
    var delta = y - lastTouchY;
    lastTouchY = y;
    var content = drawer.content;
    if (!content.contains(event.target) || content.scrollHeight <= content.clientHeight ||
        (delta > 0 && content.scrollTop <= 0) ||
        (delta < 0 && content.scrollTop + content.clientHeight >= content.scrollHeight - 1)) event.preventDefault();
  }, { passive: false });
  window.addEventListener('resize', positionDrawer);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', positionDrawer);
    window.visualViewport.addEventListener('scroll', positionDrawer);
  }
  window.addEventListener('pagehide', function () { closeDrawer(false, true); closeAll(); });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () { closeDrawer(false, true); closeAll(); });

  window.LuxsterCatalogMenuController = { init: init, close: closeAll, closeDrawer: closeDrawer };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
}());

